import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
    Instagram,
    Facebook,
    Phone,
    Mail,
    MapPin,
    Clock,
    ArrowUpRight,
    Menu,
    X,
} from "lucide-react";

/* ----------------------------------------------------------
   Brand assets
---------------------------------------------------------- */
const LOGO_URL = "/brand/logo.png";
const LOGO_LIGHT_URL = "/brand/logo_light.png";
const PROMO_BALI = "/brand/about_main.jpg";
const ITINERARY_IMG =
    "https://d268zs2sdbzvo0.cloudfront.net/681936f87b1e38363cda0780_343cb9f9-3f5d-4948-836f-7f0cdff04066_Screenshot-2026-01-27-094049.png";
const ITINERARY_IMG_2 =
    "https://d268zs2sdbzvo0.cloudfront.net/681936f87b1e38363cda0780_66c230a9-473b-4ce3-bcf3-c1a5e3219190_Itinerary-image.png";
const TRAVEL_PURPOSE_IMG =
    "https://d268zs2sdbzvo0.cloudfront.net/681936f87b1e38363cda0780_0096f659-3897-4fdb-aa53-a28b541645be_travel-on-purpose.png";
const FLOATING_BREAKFAST = "/brand/bali_breakfast.jpg";
const SLO_TABLE = "/brand/slo_about.jpg";

/* Editorial supporting imagery */
const HERO_BG =
    "https://images.pexels.com/photos/13839264/pexels-photo-13839264.jpeg";
const TAILORED_IMG =
    "https://images.unsplash.com/photo-1766214573274-f31f935eda13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjByZXNvcnQlMjBwb29sJTIwb2NlYW58ZW58MHx8fHwxNzc3NzAwNTA5fDA&ixlib=rb-4.1.0&q=85";
const WELLNESS_IMG =
    "https://images.unsplash.com/photo-1757937176646-d943553b5f09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMHNwYSUyMG5hdHVyZXxlbnwwfHx8fDE3Nzc3MDA1MDl8MA&ixlib=rb-4.1.0&q=85";
const CELEBRATION_IMG =
    "https://images.unsplash.com/photo-1768508947591-b650dacc70a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwZGlubmVyJTIwcGFydHklMjBjZWxlYnJhdGlvbnxlbnwwfHx8fDE3Nzc3MDA1MDl8MA&ixlib=rb-4.1.0&q=85";

const BOOK_URL = "https://cal.com/shawnie-fcolum/free-discovery-call";
const EMAIL = "shawnieaunnie@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/shawnieaunniepresents";
const FACEBOOK_URL = "https://www.facebook.com/Shawnieaunniepresents";
const TIKTOK_URL = "https://www.tiktok.com/@pic.n.pak.tours.a";
const CONTACT_URL = "https://form.jotform.com/241672429359162";
const INQUIRY_URL = "https://form.jotform.com/260424908291155";

/* ----------------------------------------------------------
   Reveal-on-scroll hook (IntersectionObserver)
---------------------------------------------------------- */
function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-in");
                        obs.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return ref;
}

/* ----------------------------------------------------------
   Header
---------------------------------------------------------- */
const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { id: "experiences", label: "Experiences" },
        { id: "about", label: "About" },
        { id: "process", label: "Process" },
        { id: "services", label: "Services" },
        { id: "testimony", label: "Testimony" },
        { id: "book", label: "Book" },
        { id: "contact", label: "Contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
                scrolled
                    ? "bg-brand-ivory/85 backdrop-blur-xl border-b border-brand-line"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between py-4">
                <a
                    href="#top"
                    data-testid="header-logo-link"
                    className="flex items-center"
                >
                    <img
                        src={scrolled ? LOGO_URL : LOGO_LIGHT_URL}
                        alt="Shawnie & Aunnie Presents"
                        data-testid="header-logo"
                        className={`w-auto object-contain transition-[height] duration-500 ${
                            scrolled ? "h-14 md:h-16" : "h-20 md:h-28 drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
                        }`}
                    />
                    <span className="sr-only">Shawnie &amp; Aunnie Presents</span>
                </a>

                <nav
                    aria-label="Primary"
                    className="hidden md:flex items-center gap-9"
                >
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            className="link-underline text-[0.78rem] tracking-[0.22em] uppercase text-brand-espresso/85 hover:text-brand-espresso transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <a
                    href={BOOK_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="header-book-button"
                    className="hidden md:inline-flex btn-primary"
                >
                    Book Appointment
                </a>

                <button
                    aria-label="Open menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-brand-line bg-brand-ivory/70 backdrop-blur"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {/* Mobile sheet */}
            <div
                className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
                    open ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="container-x py-6 bg-brand-ivory/95 backdrop-blur-xl border-t border-brand-line">
                    <ul className="flex flex-col gap-5">
                        {links.map((l) => (
                            <li key={l.id}>
                                <a
                                    href={`#${l.id}`}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    className="text-base tracking-[0.18em] uppercase text-brand-espresso"
                                >
                                    {l.label}
                                </a>
                            </li>
                        ))}
                        <li className="pt-3">
                            <a
                                href={BOOK_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="mobile-book-button"
                                className="btn-primary w-full"
                            >
                                Book Appointment
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </header>
    );
};

/* ----------------------------------------------------------
   Hero
---------------------------------------------------------- */
const Hero = () => {
    const ref = useReveal();
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden"
        >
            <div
                className="absolute inset-0 image-cover"
                style={{ backgroundImage: `url(${HERO_BG})` }}
                aria-hidden
            />
            <div
                className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"
                aria-hidden
            />
            <div className="absolute inset-0 grain" aria-hidden />

            <div className="relative container-x flex flex-col justify-end min-h-[100svh] pb-20 pt-40 md:pb-28">
                <div ref={ref} className="reveal max-w-4xl">
                    <p
                        className="eyebrow text-[#F2EBE1]/90"
                        data-testid="hero-eyebrow"
                    >
                        SHAWNIE &amp; AUNNIE PRESENTS...
                    </p>
                    <h1
                        data-testid="hero-headline"
                        className="font-serif text-white mt-6 text-[clamp(3rem,9vw,8.25rem)] leading-[0.95] tracking-tight"
                    >
                        Elegant.
                        <span className="italic text-[#F2EBE1]"> Classy.</span>
                        <br />
                        Timeless.
                    </h1>
                    <p
                        data-testid="hero-subline"
                        className="mt-8 max-w-xl text-base md:text-lg text-white/85 font-light leading-relaxed"
                    >
                        A luxury travel concierge & event planning house —
                        curating tailored journeys, intentional retreats, and
                        signature celebrations across the world&apos;s most
                        captivating destinations.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href={BOOK_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-book-button"
                            className="btn-primary"
                            style={{
                                background: "#FDFBF7",
                                color: "#1C1914",
                            }}
                        >
                            Book Appointment <ArrowUpRight size={16} />
                        </a>
                        <a
                            href={INQUIRY_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-contact-button"
                            className="btn-ghost-light"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>

                {/* Bottom strip */}
                <div className="relative mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 text-white/85">
                    {[
                        { k: "Est.", v: "Crafted in Los Angeles" },
                        { k: "Service", v: "Concierge by Hand" },
                        { k: "Reach", v: "Bali · Norway · Turks" },
                        { k: "Hours", v: "Mon — Sat · 9–6" },
                    ].map((item, i) => (
                        <div
                            key={i}
                            className="border-t border-white/30 pt-4"
                        >
                            <div className="text-[0.65rem] tracking-[0.32em] uppercase text-white/60">
                                {item.k}
                            </div>
                            <div className="mt-2 text-sm md:text-base font-light">
                                {item.v}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Marquee – brand values
---------------------------------------------------------- */
const Marquee = () => {
    const items = [
        "Tailored Itineraries",
        "Private Transportation",
        "Luxury Accommodations",
        "Curated Excursions",
        "Wellness Retreats",
        "Signature Celebrations",
        "Concierge by Hand",
    ];
    const Row = () => (
        <div className="marquee-track">
            {items.map((t, i) => (
                <span
                    key={i}
                    className="font-serif italic text-3xl md:text-5xl text-brand-espresso/80 whitespace-nowrap flex items-center gap-12"
                >
                    {t}
                    <span className="inline-block w-2 h-2 rounded-full bg-brand-gold" />
                </span>
            ))}
        </div>
    );
    return (
        <section
            data-testid="marquee-section"
            className="relative py-14 md:py-20 bg-brand-cream border-y border-brand-line"
        >
            <div className="marquee">
                <Row />
                <Row aria-hidden />
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Signature offerings (3 alternating editorial rows)
---------------------------------------------------------- */
const Offering = ({ index, eyebrow, title, body, image, reversed, testid, hideCta }) => {
    const ref = useReveal();
    return (
        <article
            ref={ref}
            data-testid={testid}
            className={`reveal grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                reversed ? "lg:[direction:rtl]" : ""
            }`}
        >
            <div
                className={`lg:col-span-7 [direction:ltr]`}
            >
                <div className="relative overflow-hidden rounded-sm border border-brand-line">
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-[60vh] md:h-[78vh] object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
                        loading="lazy"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-brand-ivory/85 backdrop-blur text-[0.65rem] tracking-[0.32em] uppercase text-brand-espresso">
                        {`0${index}`} / Signature
                    </div>
                </div>
            </div>
            <div className="lg:col-span-5 [direction:ltr]">
                <p className="eyebrow">{eyebrow}</p>
                <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02] text-brand-espresso">
                    {title}
                </h3>
                <p className="mt-6 text-base md:text-lg text-brand-cocoa/80 leading-relaxed font-light max-w-prose">
                    {body}
                </p>
                {!hideCta && (
                    <a
                        href={INQUIRY_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`${testid}-cta`}
                        className="link-underline mt-8 inline-flex items-center gap-2 text-[0.78rem] tracking-[0.28em] uppercase text-brand-espresso"
                    >
                        Inquire Now <ArrowUpRight size={14} />
                    </a>
                )}
            </div>
        </article>
    );
};

const Offerings = () => {
    return (
        <section
            id="experiences"
            data-testid="offerings-section"
            className="relative py-28 md:py-40 bg-brand-ivory"
        >
            <div className="container-x">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 md:mb-28">
                    <div className="lg:col-span-5">
                        <p className="eyebrow">Signature Offerings</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] text-brand-espresso">
                            Tailored.<br />
                            <span className="italic text-brand-gold-deep">
                                To you.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="text-lg md:text-xl text-brand-cocoa/80 font-light leading-relaxed">
                            Each experience is thoughtfully curated to reflect
                            your personal style, preferences, and desires —
                            beautifully styled, flawlessly executed, and built
                            to be remembered.
                        </p>
                    </div>
                </div>

                <div className="space-y-28 md:space-y-40">
                    <Offering
                        index={1}
                        testid="offering-luxury"
                        eyebrow="Tailored Luxury Experiences"
                        title="Bespoke journeys, designed to feel like yours."
                        body="From private villas overlooking the Indian Ocean to chartered escapes through the fjords — each itinerary is hand-crafted around your pace, passions, and palette. Nothing is templated. Everything is intentional."
                        image={SLO_TABLE}
                        hideCta
                    />
                    <Offering
                        index={2}
                        testid="offering-wellness"
                        eyebrow="Retreat & Wellness Experiences"
                        title="Reset, restore, return — beautifully held."
                        body="Our retreats blend serene settings, intentional wellness practices, and seamless logistics to create supportive, transformative experiences. Mindful living. Authentic connection. Refined comfort."
                        image={WELLNESS_IMG}
                        reversed
                    />
                    <Offering
                        index={3}
                        testid="offering-celebrations"
                        eyebrow="Luxury Celebrations & Social Events"
                        title="Moments worth gathering for."
                        body="From intimate dinners to grand celebrations, every event is thoughtfully planned, elegantly styled, and flawlessly executed. You arrive — we take care of everything else."
                        image={CELEBRATION_IMG}
                    />
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   About
---------------------------------------------------------- */
const About = () => {
    const ref = useReveal();
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-28 md:py-40 bg-brand-cream"
        >
            <div className="container-x">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative">
                            <img
                                src={PROMO_BALI}
                                alt="Shawnie & Aunnie in Bali"
                                className="w-full h-[60vh] md:h-[72vh] object-cover rounded-sm border border-brand-line"
                                loading="lazy"
                            />
                            <div className="hidden md:block absolute -bottom-10 -right-10 w-48 h-48 border border-brand-gold/60 rounded-sm pointer-events-none" />
                        </div>
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-6">
                        <p className="eyebrow">About Us</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] text-brand-espresso">
                            Born of friendship,
                            <span className="italic text-brand-gold-deep">
                                {" "}built on intention.
                            </span>
                        </h2>
                        <div className="mt-10 space-y-6 text-base md:text-lg text-brand-cocoa/85 font-light leading-[1.85] max-w-2xl">
                            <p>
                                Shawnie &amp; Aunnie Presents... was born from
                                friendship, creativity, and a shared passion
                                for curating extraordinary experiences. What
                                began as two visionary women combining their
                                talents has evolved into a refined luxury
                                travel concierge and event planning house.
                            </p>
                            <p>
                                Every detail — from the destination to the
                                dinner table — is considered, styled, and
                                executed with care. We design for the way you
                                want to feel, not just the places you want to
                                go.
                            </p>
                        </div>
                        <p className="mt-8 font-serif italic text-2xl text-brand-gold-deep">
                            — Shawnie &amp; Aunnie
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Planning Process
---------------------------------------------------------- */
const ProcessStep = ({ num, title, body, image, testid }) => {
    const ref = useReveal();
    return (
        <div
            ref={ref}
            data-testid={testid}
            className="reveal relative grid grid-cols-12 gap-6 items-start py-12 md:py-16 border-t border-brand-line first:border-t-0"
        >
            <div className="col-span-12 md:col-span-2">
                <div className="num-display text-[5rem] md:text-[7rem]">{num}</div>
            </div>
            <div className="col-span-12 md:col-span-6">
                <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-brand-espresso leading-[1.05]">
                    {title}
                </h3>
                <p className="mt-5 text-base md:text-lg text-brand-cocoa/80 font-light leading-relaxed max-w-xl">
                    {body}
                </p>
            </div>
            <div className="col-span-12 md:col-span-4">
                <div className="relative overflow-hidden rounded-sm border border-brand-line">
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-56 md:h-72 object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.05]"
                        loading="lazy"
                    />
                </div>
            </div>
        </div>
    );
};

const Process = () => {
    return (
        <section
            id="process"
            data-testid="process-section"
            className="relative py-28 md:py-40 bg-brand-ivory"
        >
            <div className="container-x">
                <div className="max-w-3xl mb-16">
                    <p className="eyebrow">The Planning Process</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] text-brand-espresso">
                        From concept,
                        <span className="italic text-brand-gold-deep">
                            {" "}to completion.
                        </span>
                    </h2>
                    <p className="mt-7 text-lg text-brand-cocoa/80 font-light leading-relaxed">
                        At Shawnie &amp; Aunnie Presents..., every experience
                        is meticulously designed — meaningful moments and
                        seamless logistics, executed with quiet precision.
                    </p>
                </div>

                <div>
                    <ProcessStep
                        num="01"
                        testid="process-step-1"
                        title="Start Your Journey"
                        body="Fill out our inquiry form and let us guide you toward a personalized, unforgettable experience — whether you have a destination in mind or need a little inspiration."
                        image={ITINERARY_IMG}
                    />
                    <ProcessStep
                        num="02"
                        testid="process-step-2"
                        title="Create Your Itinerary"
                        body="Our travel concierge will craft an itinerary tailored to your pace, your passions, your destination, and your desires."
                        image={ITINERARY_IMG_2}
                    />
                    <ProcessStep
                        num="03"
                        testid="process-step-3"
                        title="Travel On Purpose"
                        body="We manage every aspect of your journey — from securing luxury accommodations to arranging private transportation, excursions, and dining reservations."
                        image={TRAVEL_PURPOSE_IMG}
                    />
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Services Grid
---------------------------------------------------------- */
const Services = () => {
    const ref = useReveal();
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-28 md:py-40 bg-brand-espresso text-brand-ivory"
        >
            <div className="container-x">
                <div ref={ref} className="reveal max-w-3xl mb-16">
                    <p className="eyebrow text-brand-gold">Services</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0]">
                        What we craft,
                        <span className="italic text-brand-gold">
                            {" "}we craft fully.
                        </span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Exotic Destinations - large */}
                    <div
                        data-testid="service-exotic"
                        className="lg:col-span-7 group relative overflow-hidden rounded-sm border border-white/10 min-h-[420px] md:min-h-[560px] image-cover"
                        style={{ backgroundImage: `url(${TAILORED_IMG})` }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                        <div className="absolute inset-0 grain" />
                        <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
                            <p className="eyebrow text-brand-gold">01 — Travel</p>
                            <h3 className="font-serif text-4xl md:text-5xl mt-4">
                                Exotic Destinations
                            </h3>
                            <p className="mt-5 max-w-xl text-white/85 font-light leading-relaxed">
                                Bali. Turks &amp; Caicos. Thailand. Norway.
                                No continent is beyond our reach. Every
                                itinerary is curated to transform the world&apos;s
                                most captivating locations into extraordinary,
                                personalized experiences.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3 text-[0.7rem] tracking-[0.28em] uppercase text-white/80">
                                {[
                                    "Bali",
                                    "Turks & Caicos",
                                    "Thailand",
                                    "Norway",
                                    "+ Beyond",
                                ].map((t) => (
                                    <span
                                        key={t}
                                        className="px-3 py-1 border border-white/40 rounded-full"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Wellness */}
                    <div
                        data-testid="service-wellness"
                        className="lg:col-span-5 group relative overflow-hidden rounded-sm border border-white/10 min-h-[420px] md:min-h-[560px] image-cover"
                        style={{ backgroundImage: `url(${WELLNESS_IMG})` }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                        <div className="absolute inset-0 grain" />
                        <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
                            <p className="eyebrow text-brand-gold">02 — Wellness</p>
                            <h3 className="font-serif text-4xl md:text-5xl mt-4">
                                Wellness &amp; Retreats
                            </h3>
                            <p className="mt-5 text-white/85 font-light leading-relaxed">
                                Recharge, reconnect, restore. Our retreats
                                combine serene settings, intentional wellness
                                practices, and seamless planning for a truly
                                transformative experience.
                            </p>
                        </div>
                    </div>

                    {/* Party Planning - wide */}
                    <div
                        data-testid="service-celebrations"
                        className="lg:col-span-12 group relative overflow-hidden rounded-sm border border-white/10 min-h-[360px] md:min-h-[480px] image-cover"
                        style={{ backgroundImage: `url(${CELEBRATION_IMG})` }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
                        <div className="absolute inset-0 grain" />
                        <div className="relative h-full grid grid-cols-1 md:grid-cols-2 gap-8 p-8 md:p-12 items-end">
                            <div>
                                <p className="eyebrow text-brand-gold">
                                    03 — Celebrations
                                </p>
                                <h3 className="font-serif text-4xl md:text-5xl mt-4">
                                    Party &amp; Event Planning
                                </h3>
                                <p className="mt-5 max-w-lg text-white/85 font-light leading-relaxed">
                                    From intimate gatherings to grand
                                    celebrations, we transform your vision into
                                    unforgettable moments — thoughtfully
                                    planned, elegantly styled, flawlessly
                                    executed.
                                </p>
                            </div>
                            <div className="md:justify-self-end">
                                <a
                                    href={INQUIRY_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="services-inquire-cta"
                                    className="btn-ghost-light"
                                >
                                    Plan With Us <ArrowUpRight size={14} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Inquiry CTA
---------------------------------------------------------- */
const InquiryCTA = () => {
    const ref = useReveal();
    return (
        <section
            data-testid="inquiry-section"
            className="relative py-28 md:py-40 bg-brand-champagne overflow-hidden"
        >
            <div className="container-x">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <p className="eyebrow">Begin</p>
                        <h2 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] mt-5 leading-[0.95] text-brand-espresso">
                            Book your
                            <br />
                            <span className="italic text-brand-gold-deep">
                                vacation today.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-lg text-brand-cocoa/85 font-light leading-relaxed">
                            Tell us a little about your dream — destination,
                            occasion, or feeling — and we&apos;ll take it from
                            there.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href={INQUIRY_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="inquiry-form-cta"
                                className="btn-primary"
                            >
                                Inquiry Form <ArrowUpRight size={16} />
                            </a>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="inquiry-email-cta"
                                className="btn-outline"
                            >
                                Email Us
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { k: "Tell us your dream", v: "Inquire in 2 minutes" },
                        { k: "We design the journey", v: "Curated within days" },
                        { k: "You arrive", v: "Everything else handled" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            className="border-t border-brand-espresso/20 pt-5"
                        >
                            <div className="text-[0.7rem] tracking-[0.32em] uppercase text-brand-gold-deep">
                                Step 0{i + 1}
                            </div>
                            <h4 className="font-serif text-2xl md:text-3xl mt-3 text-brand-espresso">
                                {s.k}
                            </h4>
                            <p className="mt-2 text-sm text-brand-cocoa/75">
                                {s.v}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Contact / Footer
---------------------------------------------------------- */
const Contact = () => {
    const ref = useReveal();
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-brand-espresso text-brand-ivory pt-24 md:pt-32 pb-12"
        >
            <div className="container-x">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-6">
                        <p className="eyebrow text-brand-gold">Location & Contact</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0]">
                            Let&apos;s plan
                            <br />
                            <span className="italic text-brand-gold">
                                something beautiful.
                            </span>
                        </h2>

                        <ul className="mt-12 space-y-6 text-brand-ivory/90">
                            <li className="flex items-start gap-4">
                                <MapPin
                                    size={18}
                                    className="mt-1 text-brand-gold shrink-0"
                                />
                                <div>
                                    <div className="text-[0.7rem] tracking-[0.32em] uppercase text-white/55">
                                        Studio
                                    </div>
                                    <div
                                        data-testid="contact-location"
                                        className="font-serif text-2xl mt-1"
                                    >
                                        Los Angeles, California
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Phone
                                    size={18}
                                    className="mt-1 text-brand-gold shrink-0"
                                />
                                <div>
                                    <div className="text-[0.7rem] tracking-[0.32em] uppercase text-white/55">
                                        Phone
                                    </div>
                                    <a
                                        href="tel:+13102938722"
                                        data-testid="contact-phone"
                                        className="font-serif text-2xl mt-1 link-underline inline-block"
                                    >
                                        310 · 293 · 8722
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Mail
                                    size={18}
                                    className="mt-1 text-brand-gold shrink-0"
                                />
                                <div>
                                    <div className="text-[0.7rem] tracking-[0.32em] uppercase text-white/55">
                                        Email
                                    </div>
                                    <a
                                        href={`mailto:${EMAIL}`}
                                        data-testid="contact-email"
                                        className="font-serif text-2xl mt-1 link-underline inline-block break-all"
                                    >
                                        {EMAIL}
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Clock
                                    size={18}
                                    className="mt-1 text-brand-gold shrink-0"
                                />
                                <div>
                                    <div className="text-[0.7rem] tracking-[0.32em] uppercase text-white/55">
                                        Hours
                                    </div>
                                    <div
                                        data-testid="contact-hours"
                                        className="font-serif text-2xl mt-1"
                                    >
                                        Mon — Sat · 9:00am – 6:00pm
                                    </div>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-12 flex items-center gap-4">
                            <a
                                href={BOOK_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-book-cta"
                                className="btn-primary"
                                style={{
                                    background: "#FDFBF7",
                                    color: "#1C1914",
                                }}
                            >
                                Book Appointment
                            </a>
                            <a
                                href={INQUIRY_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-form-cta"
                                className="btn-ghost-light"
                            >
                                Contact Form
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-6">
                        <div className="relative overflow-hidden rounded-sm border border-white/10">
                            <img
                                src={FLOATING_BREAKFAST}
                                alt="Floating breakfast — Bali 2025"
                                className="w-full h-[60vh] md:h-[78vh] object-cover"
                                loading="lazy"
                            />
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                                <span className="text-[0.65rem] tracking-[0.32em] uppercase bg-black/40 backdrop-blur px-3 py-1 rounded-full">
                                    Bali · 2025
                                </span>
                                <span className="font-serif italic">
                                    Floating Breakfast
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer bottom */}
                <div className="mt-24 pt-8 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-sm text-white/65">
                    <div className="flex items-center gap-3">
                        <img
                            src={LOGO_LIGHT_URL}
                            alt="Shawnie & Aunnie Presents"
                            className="h-14 w-auto"
                        />
                        <span className="font-serif italic text-base text-white/80">
                            © {new Date().getFullYear()} Shawnie &amp; Aunnie
                            Presents
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <a
                            href="#"
                            data-testid="footer-privacy-link"
                            className="link-underline text-[0.72rem] tracking-[0.28em] uppercase"
                        >
                            Privacy Policy
                        </a>
                        <a
                            href="#"
                            data-testid="footer-terms-link"
                            className="link-underline text-[0.72rem] tracking-[0.28em] uppercase"
                        >
                            Terms
                        </a>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram"
                            data-testid="social-instagram"
                            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-white/30 hover:bg-brand-gold hover:border-brand-gold transition-colors"
                        >
                            <Instagram size={16} />
                        </a>
                        <a
                            href={TIKTOK_URL}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="TikTok"
                            data-testid="social-tiktok"
                            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-white/30 hover:bg-brand-gold hover:border-brand-gold transition-colors text-xs font-semibold tracking-wider"
                        >
                            TT
                        </a>
                        <a
                            href={FACEBOOK_URL}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Facebook"
                            data-testid="social-facebook"
                            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-white/30 hover:bg-brand-gold hover:border-brand-gold transition-colors"
                        >
                            <Facebook size={16} />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Testimony
---------------------------------------------------------- */
const Testimony = () => {
    const ref = useReveal();
    return (
        <section
            id="testimony"
            data-testid="testimony-section"
            className="relative py-28 md:py-40 bg-brand-cream"
        >
            <div className="container-x">
                <div
                    ref={ref}
                    className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start"
                >
                    <div className="lg:col-span-5 lg:sticky lg:top-32">
                        <p className="eyebrow">Testimony</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] text-brand-espresso">
                            In their
                            <br />
                            <span className="italic text-brand-gold-deep">
                                own words.
                            </span>
                        </h2>
                        <p className="mt-8 text-lg text-brand-cocoa/80 font-light leading-relaxed max-w-md">
                            Hear from the travelers and hosts we&apos;ve had
                            the joy of designing experiences for — the
                            moments, the details, the way it felt.
                        </p>
                        <span className="mt-10 divider-mark">
                            <span className="font-serif italic text-base">
                                A note from our guests
                            </span>
                        </span>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="relative overflow-hidden rounded-sm border border-brand-line bg-black shadow-[0_30px_80px_-40px_rgba(28,25,20,0.45)]">
                            <video
                                data-testid="testimony-video"
                                controls
                                playsInline
                                preload="metadata"
                                poster="/brand/testimony_poster.jpg"
                                className="w-full h-auto block aspect-[9/16] md:aspect-[3/4] object-contain bg-black"
                            >
                                <source
                                    src="/brand/testimony.mp4"
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                            <div className="absolute top-4 left-4 px-3 py-1 bg-brand-ivory/85 backdrop-blur text-[0.65rem] tracking-[0.32em] uppercase text-brand-espresso">
                                Client · Testimony
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Booking (embedded Cal.com)
---------------------------------------------------------- */
const Booking = () => {
    const ref = useReveal();
    return (
        <section
            id="book"
            data-testid="booking-section"
            className="relative py-28 md:py-40 bg-brand-ivory"
        >
            <div className="container-x">
                <div ref={ref} className="reveal max-w-3xl mb-12">
                    <p className="eyebrow">Free Discovery Call</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] text-brand-espresso">
                        Pick a time,
                        <span className="italic text-brand-gold-deep">
                            {" "}we&apos;ll do the rest.
                        </span>
                    </h2>
                    <p className="mt-7 text-lg text-brand-cocoa/80 font-light leading-relaxed">
                        Choose a day and time that suits you for a relaxed,
                        no-obligation chat about your next journey or event.
                    </p>
                </div>
                <div className="rounded-sm border border-brand-line bg-white overflow-hidden shadow-[0_30px_80px_-40px_rgba(28,25,20,0.35)]">
                    <iframe
                        data-testid="booking-calendar"
                        title="Book a free discovery call"
                        src={`${BOOK_URL}?embed=true&layout=month_view`}
                        className="w-full h-[760px] md:h-[680px] block"
                        loading="lazy"
                    />
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Trip photo strip (links to Instagram)
---------------------------------------------------------- */
const PhotoStrip = () => {
    const photos = [
        { src: PROMO_BALI, alt: "Shawnie & Aunnie in Bali" },
        { src: FLOATING_BREAKFAST, alt: "Floating breakfast in Bali" },
        { src: SLO_TABLE, alt: "Styled celebration table" },
        { src: "/brand/testimony_poster.jpg", alt: "Happy travelers" },
        { src: TRAVEL_PURPOSE_IMG, alt: "Travel on purpose" },
    ];
    return (
        <section
            data-testid="photo-strip-section"
            className="relative pt-24 md:pt-32 pb-0 bg-brand-espresso text-brand-ivory"
        >
            <div className="container-x flex flex-wrap items-end justify-between gap-6 mb-10">
                <div>
                    <p className="eyebrow text-brand-gold">On Instagram</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-4">
                        Follow the{" "}
                        <span className="italic text-brand-gold">journey.</span>
                    </h2>
                </div>
                <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="photo-strip-follow"
                    className="btn-ghost-light"
                >
                    <Instagram size={16} /> @shawnieaunniepresents
                </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5">
                {photos.map((p, i) => (
                    <a
                        key={p.src}
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`photo-strip-item-${i}`}
                        className={`group relative block aspect-square overflow-hidden ${
                            i === 4 ? "hidden md:block" : ""
                        }`}
                    >
                        <img
                            src={p.src}
                            alt={p.alt}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-[background-color] duration-500 flex items-center justify-center">
                            <Instagram
                                size={28}
                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            />
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
};

/* ----------------------------------------------------------
   Page
---------------------------------------------------------- */
const Home = () => {
    return (
        <main data-testid="home-page" className="bg-brand-ivory text-brand-espresso">
            <Header />
            <Hero />
            <Marquee />
            <Offerings />
            <About />
            <Process />
            <Services />
            <Testimony />
            <InquiryCTA />
            <Booking />
            <PhotoStrip />
            <Contact />
        </main>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
